<template lang="pug">
.video-playlist(:class="theme")
  h3(v-if="title") {{ __(title, 'auto') }}
  Carousel
    .preview(v-for="(video, i) in playlist" @click="selectVideo(i)" :class="{loading: !video.snippet}")
      .contents
        div.space
        Icon.play(name="youtube-play" fill="rgba(255,255,255,0.75)")
        p.description(v-if="video.snippet") {{ __(video.snippet.title, 'auto') }}
        p.description(v-else)
          | &nbsp;
          br
          | &nbsp;
      .background(:style="video.style")
  Modal(v-if="openModal" @close="openModal = false" :video="true" :width="theme === 'vertical' ? '380px' : null" @youTubeReady="youTubePlayer = $event")
    .videos
      YouTubeVideo(:allVideoIds="allVideoIds" :playListIndex="selectedIndex" aspectRatio="none" :autoplay="theme === 'vertical'" :class="[theme === 'vertical' ? 'videos' : 'video']" @indexChanged="selectedIndex = $event")
      ArrowNav(:modelValue="1" :max="3" prevLabel="Previous video" nextLabel="Next video" @update:modelValue="changeVideo($event)" :class="[theme === 'vertical' ? 'vertical-nav-styles' : 'horizontal-nav-styles']")
</template>
<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRogue } from '../../js/rogue'
import ArrowNav from '../core/ArrowNav.vue'

const props = defineProps({
  playlistId: {
    type     : String,
    required : true,
  },
  theme: {
    type    : String,
    default : 'vertical',
  },
  title: {
    type    : String,
    default : '',
  },
})

const rogue = useRogue()

const playlist = ref<any[]>([{}, {}, {}, {}, {}])
const openModal = ref(false)
const selectedIndex = ref(0)
const player = ref(null)
const youTubePlayer = ref(null)

const allVideoIds = computed(() => {
  const allVideoIdsArray = playlist.value.map((video) => video?.contentDetails?.videoId)
  
  return allVideoIdsArray
})

function changeVideo(value: number) {
  if (value === 2) {
    selectedIndex.value = Math.min(selectedIndex.value + 1, allVideoIds.value.length - 1)
  } else if (value === 0) {
    selectedIndex.value = Math.max(selectedIndex.value - 1, 0)
  }
}

async function load() {
  playlist.value = [{}, {}, {}, {}, {}]
  try {
    const response = await rogue?.fetch(`/svc_ssr/playlist?playlistId=${props.playlistId}`)

    if (Array.isArray(response)) {
      response.forEach(video => {
        const { maxres, standard } = video.snippet.thumbnails
        video.style = { background: `center / cover no-repeat url('${maxres?.url || standard?.url}')` }
      })
      playlist.value = response
    }
  } catch {
    playlist.value = []
    console.log('error loading shorts playlist')
  }
}

function selectVideo(index: number) {
  if (!playlist.value[index].contentDetails) {
    return
  }
  openModal.value = true
  selectedIndex.value = index
}

onMounted(() => {
  load()
})

watch(() => props.playlistId, (newPlaylistId, oldPlaylistId) => {
  if (newPlaylistId && newPlaylistId !== oldPlaylistId) {
    load()
  }
})
</script>
<style lang="scss" scoped>

@keyframes skeleton-loading {
  0% {
    background-color: $color-gray;
  }
  100% {
    background-color: $color-gray-light;
  }
}

.vertical-nav-styles {
  align-self: center;
  justify-self: center;
  width: 320px;
}

.horizontal-nav-styles {
  align-self: center;
  justify-self: center;
  min-width: 300px;
  width: 85%;
}

.preview, .background {
  height: 380px;
  width: 211px;
}

.background {
  top: 0;
  z-index: -1;
  position: absolute;
  background-color: $color-gray-light;
}

.preview {
  cursor: pointer;
  margin-right: $size-m;
  position: relative;
  overflow: hidden;
  z-index: 14;

  &:last-child {
    margin-right: 0;
  }

  &.loading {
    .background {
      animation: skeleton-loading 1s linear infinite alternate;
    }
    .play {
      display: none;
    }
  }
}

.video-playlist .contents {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: $size-s;
}

.play {
  width: 50px;
  height: 50px;
  svg {
    fill: $color-gray !important;
  }
}

.description {
  font-weight: bold;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 0px;
}

.vertical {
  .contents {
    height: 100%;
  }
  .description {
    color: white;
  }
  .videos {
    height: 700px;
  }
}

.landscape {
  .space, .play {
    display: none;
  }

  .video {
    width: 100%;
    min-height: 500px;
    max-height: 100%;
  }

  .preview {
    display: flex;
    flex-direction: column-reverse;
    width: 308px;
    height: auto;
    &.skeleton {
      height: 172px;
    }
  }

  .contents {
    padding: $size-s 0;
    min-height: 80px;
  }

  .background {
    width: 308px;
    height: 172px;
    position: relative;
    z-index: 1;
  }
}

</style>
